/**
 * walletErrorMapper — Maps raw Solana wallet SDK error strings to short, human-readable messages.
 *
 * Design rules:
 * - NEVER return the raw error string (may contain SDK internals, signatures, or stack traces)
 * - Always return a short, user-facing message
 * - Pattern match case-insensitively via .toLowerCase()
 * - Covers 5 known Solana wallet error categories + a catch-all fallback
 */

/**
 * Map a raw wallet/SDK error message to a user-friendly string.
 *
 * @param raw - The raw error message from the wallet SDK or Solana RPC.
 * @returns A short, human-readable error string suitable for display in the UI.
 */
export function mapWalletError(raw: string): string {
  const lower = raw.toLowerCase();

  // User rejected / denied the transaction
  if (lower.includes("user rejected") || lower.includes("user denied") || lower.includes("rejected by user")) {
    return "Transaction cancelled";
  }

  // Insufficient funds
  if (lower.includes("insufficient") || lower.includes("not enough")) {
    return "Insufficient balance";
  }

  // Transaction expired (blockhash too old)
  if (lower.includes("blockhash") || lower.includes("block height exceeded")) {
    return "Transaction expired — please try again";
  }

  // Network / connectivity issues
  if (lower.includes("network error") || lower.includes("failed to fetch") || lower.includes("timeout")) {
    return "Network error — check your connection";
  }

  // Already submitted / duplicate transaction
  if (lower.includes("already processed") || lower.includes("duplicate")) {
    return "Transaction already submitted";
  }

  // Catch-all fallback — never expose the raw string
  return "Purchase failed — please try again";
}
